import React, { useState, useEffect } from 'react';
import { useUIStore } from '../../stores/useUIStore';
import { useChatStore } from '../../stores/useChatStore';
import { useDataStore } from '../../stores/useDataStore';
import { useAuthStore } from '../../stores/useAuthStore';
import toast from 'react-hot-toast';
import Spinner from '../common/Spinner';
import { getMediaUrl, DEFAULT_AVATAR_URL, withImageFallback } from '../../utils/imageUtils';

const ForwardPostModal = () => {
  const modals = useUIStore((state) => state.modals);
  const closeModal = useUIStore((state) => state.closeModal);
  const conversations = useChatStore((state) => state.conversations);
  const fetchConversations = useChatStore((state) => state.fetchConversations);
  const forwardPost = useDataStore((state) => state.forwardPost);
  const currentUser = useAuthStore((state) => state.currentUser);

  const [selectedChats, setSelectedChats] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const isOpen = modals.forwardPost?.isOpen || false;
  const postToForward = modals.forwardPost?.data?.post || null;

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      setSelectedChats([]); // Reset selection when modal opens
    }
  }, [isOpen, fetchConversations]);

  const handleSelectChat = (chatId) => {
    setSelectedChats((prev) =>
      prev.includes(chatId)
        ? prev.filter((id) => id !== chatId)
        : [...prev, chatId]
    );
  };

  const handleSend = async () => {
    if (selectedChats.length === 0) {
      toast.error('Selecciona al menos una conversación.');
      return;
    }
    if (!postToForward) return;

    setIsSending(true);
    try {
      const result = await forwardPost(postToForward._id, selectedChats);
      if (result.success) {
        closeModal('forwardPost');
      }
    } catch (error) {
      // The store handles the toast error
      console.error('Error forwarding post:', error);
    } finally {
      // This block will run regardless of success or failure
      setIsSending(false);
    }
  };

  if (!isOpen || !postToForward) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Reenviar Publicación</h2>
          <button onClick={() => closeModal('forwardPost')} className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">&times;</button>
        </div>

        <div className="p-4 max-h-80 overflow-y-auto">
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">Selecciona a quién quieres enviar esto:</p>
          <div className="space-y-2">
            {conversations.map((convo) => {
              const otherUser = convo.participants.find(p => p._id !== currentUser._id);
              if (!otherUser) return null;

              const isSelected = selectedChats.includes(convo._id);

              return (
                <div
                  key={convo._id}
                  onClick={() => handleSelectChat(convo._id)}
                  className={`flex items-center p-2 rounded-lg cursor-pointer ${isSelected ? 'bg-blue-100 dark:bg-blue-900/40' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className="mr-3 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                  />
                  <img
                    src={getMediaUrl(otherUser.avatar, DEFAULT_AVATAR_URL)}
                    alt={otherUser.username}
                    className="w-10 h-10 rounded-full mr-3"
                    onError={(event) => withImageFallback(event, DEFAULT_AVATAR_URL)}
                  />
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{otherUser.username}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={handleSend}
            disabled={isSending || selectedChats.length === 0}
            className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center"
          >
            {isSending ? (
              <>
                <Spinner size="w-5 h-5" color="border-white" />
                <span className="ml-2">Enviando...</span>
              </>
            ) : (
              'Enviar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForwardPostModal;